import {
  type ImageElement,
  type ImageMask,
  MASK_PRESETS,
  maskPath,
  type ShapeElement,
  sameMask,
} from "@cva/shared";
import { cn } from "../lib/utils";
import { useEditorApi } from "./store";

/** Small outline of a mask for pickers and the frame list. */
export function MaskIcon({ mask, size = 22 }: { mask: ImageMask | undefined; size?: number }) {
  const inset = 2;
  const inner = size - inset * 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      {mask ? (
        <path
          d={maskPath(mask, inner, inner)}
          transform={`translate(${inset} ${inset})`}
          fill="currentColor"
          fillOpacity={0.25}
          stroke="currentColor"
          strokeWidth={1.25}
          strokeLinejoin="round"
        />
      ) : (
        <rect
          x={inset}
          y={inset}
          width={inner}
          height={inner}
          fill="currentColor"
          fillOpacity={0.25}
          stroke="currentColor"
          strokeWidth={1.25}
        />
      )}
    </svg>
  );
}

/**
 * "Form" of an image or an image placeholder (frame): grid of masks, "Keine" =
 * the rectangle with its corner radius (placeholders: their own shape).
 */
export function MaskPicker({ element }: { element: ImageElement | ShapeElement }) {
  const store = useEditorApi();
  const set = (mask: ImageMask | undefined) =>
    store.getState().update((draft) => {
      const target = draft.elements[element.id];
      if (target?.type !== "image" && target?.type !== "shape") return;
      if (mask) target.mask = { ...mask };
      else delete target.mask;
    });
  const options = [{ id: "none", label: "Keine", mask: undefined }, ...MASK_PRESETS];
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-zinc-500">Form</div>
      <div className="grid grid-cols-6 gap-1">
        {options.map((option) => {
          const active = sameMask(option.mask, element.mask);
          return (
            <button
              key={option.id}
              type="button"
              title={option.label}
              aria-label={`Form: ${option.label}`}
              aria-pressed={active}
              onClick={() => set(option.mask)}
              className={cn(
                "flex h-8 items-center justify-center rounded-md border text-zinc-700 hover:bg-zinc-50",
                active ? "border-brand-500 bg-brand-50 text-brand-700" : "border-zinc-200",
              )}
            >
              <MaskIcon mask={option.mask} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
